import { localized, t } from '@weavedev/lit-i18next';
import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, queryAll, state } from 'lit/decorators.js';
import { Ref, createRef, ref } from 'lit/directives/ref.js';

import { PrintColorsService } from '@/api-sdk';
import { FgAutocomplete } from '@/components/autocomplete/fg-autocomplete';
import { autoCompleteValidation } from '@/components/autocomplete/utils';
import { GlobalComponentType, global } from '@/context';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles, buttonStyles, stepContainerStyles } from '@/styles';
import { ErrorType, OptGroup, SelectOption, SelectVersion } from '@/types';
import { consume } from '@lit/context';
import '../../autocomplete/fg-autocomplete';
import '../../tooltip/fg-tooltip';
import { resetUploadFile } from '../utils';
import './fg-personalisation-file';

@customElement('fg-personalisation-choose-colors-step')
@localized()
export class FGPersonalisationChooseColor extends LitElement {
  @queryAll('fg-select')
  private inputs?: NodeListOf<FgAutocomplete>;

  @property({ type: String })
  fileName?: string;

  @property({ type: Number })
  colorAmount?: number | null = 0;

  @state()
  private colorGroups: OptGroup[] | undefined = [];

  @state()
  private colors: SelectOption[] = [];

  @state()
  private errorMessage?: string;

  @state()
  private colorData: string[] = [];

  @consume({ context: global.context })
  globalContext!: GlobalComponentType;

  private tooltipBoundaryRef: Ref<HTMLElement>[] = [];

  private processPMSColors(event: Event) {
    event.preventDefault();

    if (this.inputs && this.inputs?.length > 0) {
      const errors: {
          [key: number]: string;
        } = {
          [ErrorType.AUTOCOMPLETE_EMPTY]: t('errors.emptyColor'),
          [ErrorType.AUTOCOMPLETE_INVALID]: t('errors.invalidColor'),
        },
        autoCompleteData = autoCompleteValidation(
          this.inputs,
          this.colors?.map(item => item.value?.toString()),
          true,
        ),
        error = autoCompleteData.error;

      if (error) {
        this.errorMessage = errors[error];
        return;
      }

      this.errorMessage = '';

      if (!this.errorMessage) {
        this.colorData = autoCompleteData.items;

        this.dispatchEvent(
          new CustomEvent('personalisation-colors', {
            detail: {
              colors: this.colorData,
            },
          }),
        );
      }
    }
  }

  async firstUpdated() {
    const { locale } = this.globalContext;
    try {
      const colorGroups = await PrintColorsService.getShowPrintColors({
        locale,
      });
      const mappedColorGroups = colorGroups.map(colorGroup => ({
        label: colorGroup.label,
        value: colorGroup.label ?? 'PMS',
        colors: colorGroup.colors,
      }));
      this.colorGroups = mappedColorGroups;
      this.colors = mappedColorGroups.flatMap(colorGroup =>
        colorGroup.colors.map(color => ({
          visual: color.hexColor,
          text: color.name,
          value: color.name,
          groupName: colorGroup.value,
        })),
      );
    } catch (error) {
      console.error(
        'Something whent wrong while fetching the PMS Colors',
        error,
      );
    }
  }

  render() {
    return html`
      <div class="step-container color-step-container">
        <fg-personalisation-file
          .fileName=${this.fileName}
          @personalisation-reset-upload=${(event: CustomEvent) =>
            resetUploadFile(this, event)}
        ></fg-personalisation-file>
        <form
          class="colors-selection"
          @submit=${(event: Event) => {
            this.processPMSColors(event);
          }}
        >
          ${[...Array(this.colorAmount)].map((_, i) => {
            this.tooltipBoundaryRef[i] = createRef();
            const wrapperRef = ref(this.tooltipBoundaryRef[i]);
            return html`
              <div class="colors-selection__color" ${wrapperRef}>
                <label class="pms-color-label">
                  ${t('content.personalisation.chooseColorsStep.pmsColor', {
                    colorIndex: i + 1,
                  })}
                </label>
                <fg-select
                  version=${SelectVersion.COLOR_SELECT}
                  .optGroupField=${'groupName'}
                  .optGroups=${this.colorGroups}
                  .maxOptions=${2000}
                  .options=${this.colors}
                  .placeholder=${t(
                    'content.personalisation.chooseColorsStep.choosePmsColor',
                  ) || ''}
                  autocomplete
                  computePositioningEnabled
                ></fg-select>
                <fg-tooltip
                  .parentRef=${this.tooltipBoundaryRef[i]}
                  .tooltipData=${t('tooltips.pmsColors') || ''}
                ></fg-tooltip>
              </div>
            `;
          })}
          ${this.errorMessage
            ? html`
                <span class="error-message">${this.errorMessage}</span>
              `
            : nothing}
          <button type="submit" class="button button-primary">
            ${t('actions.completeStep')}
          </button>
        </form>
      </div>
    `;
  }

  static styles = [
    baseStyles,
    buttonStyles,
    stepContainerStyles,
    css`
      fg-personalisation-file {
        margin-bottom: 30px;
      }
      .colors-selection__color {
        align-items: center;
        display: grid;
        gap: 8px;
        grid-template-columns: auto 220px 20px;
        margin-top: 13px;
        position: relative;
      }

      .colors-selection__color:last-of-type {
        margin-bottom: 0;
      }
      .pms-color-label {
        font-weight: 600;
        font-size: 1rem;
        line-height: 1.1875rem;
      }
      .button.button-primary {
        display: block;
        margin: 20px auto 0;
        min-width: 310px;
      }
      .error-message {
        display: block;
        color: var(--fg-error--dark, var(--fg-fallbak-error--dark));
        margin-top: 20px;
        font-size: 1rem;
        width: 100%;
        text-align: center;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .colors-selection__color {
          display: grid;
          grid-template-columns: auto 280px 20px;
        }
      }
    `,
  ];
}
